/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useMemo, useState, useRef, useEffect } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import {
    useGetBillingInfoQuery,
    useGetPricingInfoQuery,
    useAddCheckoutPaymentMutation,
    useAddCheckoutPaymentConfirmMutation
} from "../../../services/paymentService";
import { useGetDoctorQuery } from "../../../services/dataService";
import {
    useAddOrderMutation,
    useLazyGetVoucherValidityQuery,
} from "../../../services/orderService";
import { AppointmentContext } from "../../../providers/AppointmentProvider";
import parseError from "../../../utils/errorUtils";

import { AppointmentCheckoutFormFooter } from "../../appointment/AppointmentCheckoutForm";
import Collapsible from "../../../components/collapsible/Collapsible";
import Avatar from "../../../components/avatar/Avatar";
import Price from "../../../components/price/Price";
import { Form, TextField } from "../../../components/forms";
import CheckoutForm from "./CheckoutForm";
import { useModal } from "../../../hooks/modalsHooks";
import GoBack from "../../../components/GoBack";
import { useLoggerUtils } from "../../../utils/loggerUtils";

const fonts = [
    {
        cssSrc: "https://fonts.googleapis.com/css2?family=Titillium+Web:wght@300;400;600&display=swap",
        family: "Titillium Web",
    },
];

export default function CheckoutPage() {
    const leftRef = useRef(null);
    const { appointmentData } = useContext(AppointmentContext);
    const { showModal, hideModal } = useModal();
    const [isSubmitting, setIsSubmitting] = useState(false);


    const navigate = useNavigate();

    const [couponId, setCouponId] = useState("");
    const [discount, setDiscount] = useState(0);

    const { data: doctorData } = useGetDoctorQuery(
        { doctorId: appointmentData.doctorId },
        { skip: !appointmentData?.doctorId }
    );
    const [getVoucherValidity] = useLazyGetVoucherValidityQuery();
    const [addOrder] = useAddOrderMutation();
    const { data: billingInfo } = useGetBillingInfoQuery();
    const { info } = useLoggerUtils();
    const [selectedOption, setSelectedOption] = useState("SUBSCRIPTION");
    const { data: subscriptionPricing, isSubscriptionLoading } = useGetPricingInfoQuery({
        type: "Subscription"
    });
    const { data: initialPricing, isInitialLoading } = useGetPricingInfoQuery({
        type: "Initial"
    });
    const [addStripeCheckout] = useAddCheckoutPaymentMutation();

    const handleRadioChange = (option) => {
        setSelectedOption(option);
    };
    const baseURL = window.location.href.split('onboarding/')[0];

    const handleCheckVoucherValidity = async (values) => {
        try {
            const res = await getVoucherValidity({ voucherId: values.voucher });

            if (res.error) {
                // handle error
                toast.error(parseError(res.error, "Couldn't submit voucher"));
                setCouponId(null);
            } else {
                // handle success
                if (res.data.valid) {
                    setCouponId(values.voucher);
                    const tempAppointmentPrice = billingInfo?.appointmentPrice;

                    const finalValue =
                        tempAppointmentPrice * res.data.percentage;

                    setDiscount(finalValue);
                    toast.success("Voucher added successfully");
                } else {
                    setCouponId(null);
                    toast.error(parseError(res.error, "The discount code provided is not applicable or valid for use."));
                }
            }
        } catch (error) {
            setCouponId(null);
            console.error(error);
        }
    };

    const checkOut = async () => {
        localStorage.setItem("paymentMode", selectedOption)
        try {
            const data = {
                quantity: 1,
                customerId: appointmentData?.patient?.id,
                successUrl: `${baseURL}order/payment-success?isCallToBook=true`,
                cancelUrl: `${baseURL}onboarding/appointment/checkout`,
                orderItemId: appointmentData?.patient?.id, //order item will be replaced from the back end with checkout confirm API call 
                description: "call to book",
                paymentMode: selectedOption === "SUBSCRIPTION" ? "SUBSCRIPTION" : "PAYMENT"
            }

            const res = await addStripeCheckout({ values: data });

            if (res.error) {
                setIsSubmitting(false);
                toast.error(parseError(res.error, "Stripe checkout failed"));
            } else {
                setIsSubmitting(false);
                localStorage.setItem("sessionId", res.data.sessionId)
                res.data && (window.location.href = res.data.url)
            }

        } catch (e) {
            setIsSubmitting(false);
            info("addOrder - catch: " + JSON.stringify(e));
        }
    }

    const stripePromise = useMemo(async () => {
        if (billingInfo) {
            return await loadStripe(billingInfo.publicKey);
        }

        return null;
    }, [billingInfo]);

    const appointmentPrice = useMemo(() => {
        const tempAppointmentPrice = billingInfo?.appointmentPrice;

        if (discount > 0) {
            return tempAppointmentPrice - discount;
        }

        return tempAppointmentPrice;
    }, [billingInfo, discount]);

    return (
        <Elements stripe={stripePromise} options={{ fonts }}>
            <div className="checkout-page row mx-0">
                <div className="container-left pt-10 bg-light-gray h-100">
                    <div className="row mx-0 mb-0 mb-md-5">
                        <div className="col-12 col-lg-11 col-xl-9 col-xxl-8 m-auto pb-5 pb-md-6 pb-xxl-10">
                            <GoBack />
                            <div className="mb-5 mt-5">
                                <Collapsible
                                    title="Consultation Summary"
                                    className="py-6 px-5 custom-shadow bg-gray-card"
                                    expanded
                                >
                                    <div className="text-primary fs-6 fw-bold">
                                        Consultation details
                                    </div>
                                    <div className="d-flex align-items-center justify-content-between mt-4">
                                        <div className="d-flex align-items-sm-center">
                                            <div
                                                className="d-none d-sm-block"
                                                style={{
                                                    width: 56,
                                                    height: 56,
                                                }}
                                            >
                                                <Avatar
                                                    src={
                                                        doctorData?.settings
                                                            ?.DOCTOR_AVATAR ||
                                                        "/images/samples/doctor-thumb.png"
                                                    }
                                                />
                                            </div>

                                            <div className="ms-0 ms-sm-4">
                                                <div className="primary-title fw-bold fs-5">
                                                    {doctorData?.displayName}
                                                </div>
                                                {appointmentData?.datetime && (
                                                    <div className="secondary-title text-dark-3 fw-light">
                                                        {dayjs(
                                                            appointmentData.datetime
                                                        ).format("DD/MM/YYYY")}
                                                        &nbsp; at&nbsp;
                                                        {dayjs(
                                                            appointmentData.datetime
                                                        ).format("hh:mma")}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {appointmentData?.notes && (
                                        <>
                                            <hr />
                                            <div className="text-primary fs-6 fw-bold">
                                                Consultation notes
                                            </div>
                                            <div className="d-flex align-items-center justify-content-between mt-2">
                                                <div className="text-dark-4 pe-2">
                                                    {appointmentData.notes}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </Collapsible>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container-right pt-10 px-2 px-lg-6 bg-white">
                    <div className="row mx-0 mb-0 mb-md-5">
                        <div className="container-right-scroll-area col-12 col-lg-11 col-xl-12 col-xxl-11 m-auto pb-5 pb-md-5 pb-xxl-5">
                            <>
                                <div className="new-order-heading px-0">
                                    Payment details
                                </div>
                                <div className="ps-0 last-order-subheading w-100 mb-5">
                                    Your payment will not be charged until your prescription is approved.
                                </div>
                            </>
                            <Collapsible
                                title="Payment Summary"
                                expanded
                                className="py-6 px-5 custom-shadow"
                            >
                                <div>
                                    <div className="d-flex mb-3 align-items-center justify-content-between text-medium-gray fs-6">
                                        <div>Video Consultation</div>
                                        <div>
                                            <Price
                                                amount={appointmentPrice}
                                                currency="GBP"
                                            />
                                        </div>
                                    </div>
                                    <hr />
                                    <div className="d-flex align-items-center fw-bold justify-content-between text-primary fs-5">
                                        <div>Total</div>
                                        <div>
                                            <Price
                                                amount={appointmentPrice}
                                                currency="GBP"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Collapsible>
                            <div className="new-order-heading px-0 mt-6 mb-4">
                                Select pricing plan*
                            </div>
                            <div className="row gap-2 gap-lg-0">
                                <div className="column col-12 col-lg-6 col-xl-6 pt-0 pb-2 pe-lg-2 cursor-pointer" onClick={() => handleRadioChange("SUBSCRIPTION")}>
                                    <div className={`px-0 d-flex flex-column justify-content-between pt-3 pb-3 rounded-1 w-100 h-100 ${selectedOption === "SUBSCRIPTION" ? "hr-radio-selected" : "hr-radio"}`}>
                                        <div ref={leftRef} className="d-flex flex-column px-4 align-items-start justify-content-between">
                                            <div className="form-check form-check-inline">
                                                <input className="form-check-input" type="radio" name="monthly" value="monthly"
                                                    checked={selectedOption === "SUBSCRIPTION"}
                                                    onChange={() => handleRadioChange("SUBSCRIPTION")} />
                                                <label className="form-check-label text-primary fs-6 lh-sm">Monthly Clinic Subscription</label>
                                            </div>
                                            <div className="d-flex flex-row gap-2 align-items-center justify-content-between mt-2">
                                                <p className="mb-0 text-primary fw-bold fs-5">
                                                    £{subscriptionPricing?.amount || "14.5"}
                                                    <span class="small fw-normal opacity-75 size-14"> / month</span>
                                                </p>
                                            </div>
                                            <div className="d-flex flex-row gap-2 align-items-center justify-content-between">
                                                <p className="mb-0 text-primary fw-bold size-14">
                                                    *£{"11.5"}<span class="size-14 fw-normal opacity-75"> / month from the 2nd year onwards</span>
                                                </p>
                                            </div>
                                        </div>
                                        <hr className="my-4 hr-radio" />
                                        <p className="mb-3 px-4 text-primary fw-bold">
                                            This subscription includes all of the following services:
                                        </p>
                                        <div className="d-flex flex-column mb-2 px-4 align-items-start justify-content-between">
                                            <ul className="d-flex flex-column gap-2 list-unstyled mb-0">
                                                <li>
                                                    <span>✔<span className="text-primary fw-bold ms-2">FREE </span></span>
                                                    Initial consultation
                                                </li>
                                                <li>
                                                    <span>✔<span className="text-primary fw-bold ms-2">FREE </span></span>
                                                    Monthly repeat prescription
                                                </li>
                                                <li className="mb-1">
                                                    <span>✔<span className="text-primary fw-bold ms-2">FREE </span></span>
                                                    6 month follow-up consultation
                                                </li>
                                            </ul>
                                        </div>
                                        <div className="flex-row gap-2 mx-4 align-items-center justify-content-between mt-auto">
                                            <p className="mb-0 text-primary text-center px-2 radio-btn-tag rounded-3 fw-bold w-100">
                                                • <span class="small fw-normal ms-1">Minimum 12-month commitment</span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="column col-12 col-lg-6 col-xl-6 pt-0 pb-2 ps-lg-2 cursor-pointer" onClick={() => handleRadioChange("PAYMENT")}>
                                    <div className={`px-0 d-flex flex-column justify-content-between pt-3 pb-3 rounded-1 w-100 h-100 ${selectedOption === "PAYMENT" ? "hr-radio-selected" : "hr-radio"}`}>
                                        <div className="d-flex flex-column px-4 align-items-start">
                                            <div className="form-check form-check-inline">
                                                <input className="form-check-input" type="radio" name="perConsultation" value="perConsultation"
                                                    checked={selectedOption === "PAYMENT"}
                                                    onChange={() => handleRadioChange("PAYMENT")}
                                                />
                                                <label className="form-check-label text-primary fs-6 lh-sm">Pay As You Go</label>
                                            </div>
                                            <div className="d-flex flex-row gap-2 align-items-center justify-content-between mt-2">
                                                <p className="mb-0 text-primary fw-bold fs-5 line lh-1">
                                                    £{initialPricing?.amount || "69"}
                                                    <span class="small fw-normal opacity-75 size-14"> - Pay for your initial consultation now</span>
                                                </p>
                                            </div>
                                        </div>
                                        <hr className="my-4 hr-radio" />
                                        <p className="mb-3 px-4 text-primary fw-bold">
                                            Pay As You Go for each of the following services:
                                        </p>
                                        <div className="d-flex flex-column mb-2 px-4 align-items-start justify-content-between">
                                            <ul className="d-flex flex-column gap-2 list-unstyled mb-0">
                                                <li>
                                                    <span>✔<span className="text-primary fw-bold ms-2">
                                                        £{initialPricing?.amount || "69"} - </span>
                                                    </span>
                                                    Initial consultation
                                                </li>
                                                <li className="d-flex gap-2">
                                                    <span>✔</span>
                                                    <div>
                                                        <span className="text-primary fw-bold">£{"9.99"} <span class="size-14 fw-normal">(Repeat each month)</span> - </span>
                                                        Repeat prescription
                                                    </div>
                                                </li>
                                                <li className="mb-1 d-flex gap-2">
                                                    <span>✔</span>
                                                    <div>
                                                        <span className="text-primary fw-bold">£{"49.99"} - </span>
                                                        6 month follow-up consultation
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>
                                        <div className="flex-row gap-2 mx-4 align-items-center justify-content-between mt-1">
                                            <p className="mb-0 text-primary text-center px-2 radio-btn-tag rounded-3 fw-bold w-100">
                                                • <span className="small fw-normal ms-1">No monthly commitment</span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <Form
                                initialValues={{
                                    terms_and_conditions: "",
                                }}
                                disabled={isSubmitting}
                                onSubmit={checkOut}
                            >
                                <AppointmentCheckoutFormFooter
                                    buttonText="Confirm Payment"
                                />
                            </Form>
                        </div>
                    </div>
                </div>
            </div>
        </Elements>
    );
}
